import {
  Datagrid,
  DateField,
  EditButton,
  FunctionField,
  List,
  TextField,
  TextInput,
  type RaRecord,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';

/**
 * Affiliate networks — READ-ONLY reference data (D-networks-readonly). Per
 * D-afrofiliate, Afrofiliate is the sole tracking platform going forward and the
 * other networks are legacy / reference, so networks are a reference LIST, not a
 * managed entity: list + detail only, NO create / edit / delete / active toggle.
 *
 * Networks are market-agnostic (the `AffiliateNetwork` type has no `market`
 * field), so there is no region filter. `q` matches the name. `storeCount` is the
 * provider-derived number of merchant_regions on the network.
 */

const networkFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
];

/** The list itself. Exported for testing without the access wrapper. */
export const NetworkListView = () => {
  const { role, accessibleRegions } = useAccessContext();
  // The Edit affordance is super_admin-only (Settings S2, networks:write). A
  // viewer's read-everywhere '*:read' does not surface it; the list stays a
  // read-only reference for everyone else.
  const canWrite = hasAccess(
    { role, accessibleRegions },
    { capability: 'networks:write' },
  );
  return (
    <List
      resource="affiliateNetworks"
      title="Networks"
      filters={networkFilters}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      {/* Clicking a row opens the read-only detail; the per-row Edit is the S2
          promotion, gated networks:write. No bulk actions, no create, no delete. */}
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="name" label="Name" />
        <TextField source="trackingDomain" label="Tracking domain" />
        <FunctionField
          label="Stores"
          sortBy="storeCount"
          render={(record: RaRecord) => String(record.storeCount ?? 0)}
        />
        <DateField source="lastImportedAt" label="Last imported" />
        {canWrite && <EditButton />}
      </Datagrid>
    </List>
  );
};

/** Gated entry point registered as the `affiliateNetworks` Resource's list. */
export const NetworkList = () => (
  <RequireAccess capability="networks:read">
    <NetworkListView />
  </RequireAccess>
);
